// Copyright (c) Docugami, Inc. All rights reserved.

import { humanBlue, machineGreen } from "components/shell/presentation/AppStyles";
import { useAppSelector } from "hooks/redux";
import { useAllBindingsForDocumentAreForProvider, useAllBindingsForEntryConfirmed, useEntryBindingsForDocumentConfirmed, useLockAllEntryBindings, useLockDocumentBindings } from "hooks/reports";
import { BindingProviderId, ReportEntry, ReportViewContext } from "networking/reportsApi";
import { selectActiveTrainingEntryIds, selectClusterId, selectReportContext } from "store/reports/selectors";
import styled from "styled-components";
import { AutomationId } from "utils/Automation";
import { ConfirmationButton } from "./ConfirmationButton";

const ConfirmationSpacer = styled.div`
    margin: 5px 2px;
`

export interface DocumentBindingsConfirmationButtonProps {
    folderId: string;
    entryIds: string[];
    readOnly?: boolean;
}

export const DocumentBindingsConfirmationButton = (props: DocumentBindingsConfirmationButtonProps): JSX.Element => {
    const context = useAppSelector(selectReportContext);
    const clusterId = useAppSelector(selectClusterId);

    const allBindingsConfirmed = useEntryBindingsForDocumentConfirmed(props.folderId, props.entryIds);
    const allBindingsAreUserProvided = useAllBindingsForDocumentAreForProvider(props.folderId, BindingProviderId.User)
    const { confirming, confirm, isDisabled } = useLockDocumentBindings(clusterId, props.folderId, props.entryIds);

    function getTooltip() {
        if (allBindingsConfirmed === undefined) {
            return;
        }
        else if (confirming) {
            return 'Completing all items for this document';
        }
        else if (allBindingsConfirmed === true) {
            return 'All items in this document are complete';
        }
        else if (!!props.readOnly) {
            return 'Some items in this document are not completed';
        }
        else {
            return 'Complete all items for this document?';
        }
    }

    function onConfirm(ev?: React.MouseEvent<HTMLDivElement>) {
        if (!isDisabled) {
            ev?.stopPropagation();
            confirm();
        }
    }

    return (
        <ConfirmationSpacer>
            <ConfirmationButton
                isConfirmed={allBindingsConfirmed === true}
                isConfirming={confirming}
                onClick={!props.readOnly ? onConfirm : undefined}
                confirmedBackgroundColor={allBindingsAreUserProvided ? humanBlue : machineGreen}
                tooltip={getTooltip()}
                readOnly={props.readOnly}
                hidden={isDisabled || context !== ReportViewContext.Review}
                data-automation-id={allBindingsConfirmed === true ? AutomationId.WorkPathConfirmedItemButton : AutomationId.WorkPathUnconfirmedItemButton} />
        </ConfirmationSpacer>
    )
}


export interface EntryColumnConfirmationButtonProps {
    entry: ReportEntry
}

export const EntryColumnConfirmationButton = (props: EntryColumnConfirmationButtonProps): JSX.Element => {
    const context = useAppSelector(selectReportContext);
    const clusterId = useAppSelector(selectClusterId);

    const allBindingsConfirmed = useAllBindingsForEntryConfirmed(props.entry.id);
    const { confirming, confirm, isDisabled } = useLockAllEntryBindings(clusterId, props.entry.id);
    const trainingEntryIds = useAppSelector(state => selectActiveTrainingEntryIds(state, props.entry.reportId, clusterId));

    function getTooltip() {
        if (allBindingsConfirmed === undefined) {
            return;
        }
        else if (confirming) {
            return 'Completing all cells in this column';
        }
        else if (allBindingsConfirmed === true) {
            return 'All cells in this column are complete';
        }
        else {
            return trainingEntryIds[props.entry.id] ? 'Complete all cells in this column? We are actively learning from your feedback to improve results.' : 'Complete all cells in this column?';
        }
    }

    return (
        <ConfirmationSpacer>
            <ConfirmationButton
                isConfirmed={allBindingsConfirmed === true}
                isConfirming={confirming}
                onClick={confirm}
                confirmedBackgroundColor={machineGreen}
                tooltip={getTooltip()}
                hidden={isDisabled || context !== ReportViewContext.Review}
                data-automation-id={allBindingsConfirmed === true ? AutomationId.WorkPathConfirmedItemButton : AutomationId.WorkPathUnconfirmedItemButton}
                pulse={trainingEntryIds[props.entry.id] && allBindingsConfirmed !== true} />
        </ConfirmationSpacer>
    )
}
